import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ChatHeader from "~/components/aiChat/components/ChatHeader";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import ChatInput from "~/components/aiChat/components/ChatInput";
import ChatMessage from "~/components/aiChat/components/ChatMessage";
import aiChatStyles from "~/components/aiChat/styles.module.scss";
import type { ChatMessage as ChatMessageType } from "~/components/aiChat/types";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import SystemProposalDisplay from "~/components/Document/SystemProposalDisplay";
import { TOAST_MESSAGES } from "~/constants/messages";
import { sampleChatHistory, sampleSystemProposalData } from "~/data/sampleProject";
import { useTutorialAccessGuard } from "~/hooks/useTutorialAccessGuard";
import type { RootState } from "~/store";
import { TUTORIAL_ACTIVE_FLAG } from "~/utils/tutorialEvents";
import styles from "./index.module.scss";

const sampleAiResponses = [
  "現在の議論を踏まえると、**顧客管理アプリ**には以下のフィールド追加が有効です。\n\n- 顧客ステータス（取引継続/休眠/要フォロー）\n- 最終接触日（自動更新）\n- 担当営業メモ（Markdown対応）\n\nこれらを追加することで、ナレッジで整理した「営業活動の棚卸し」をアプリ内で再現できます。",
  "ナレッジに登録済みの _kintoneアプリ構造テンプレート_ を活用すると、関連レコード一覧を使った案件追跡フローをすぐに反映できます。必要ならテンプレートを開いて差分だけ調整しましょう。",
  "非機能要件では、月末のアクセス集中に備えた **50ユーザー同時アクセス** の前提が定義されています。チャット内で調整した内容は要件書の該当セクションにも自動反映できます。",
];

const mapSampleMessage = (message: (typeof sampleChatHistory)[number]): ChatMessageType => ({
  id: message.id,
  content: message.content,
  sender: message.role,
  timestamp: new Date(message.timestamp),
});

const sampleDocumentMeta = {
  id: "tutorial-system-proposal-001",
  name: "システム概要書",
  type: "system-proposal",
  created_at: "2024-01-15T10:00:00Z",
  modified_at: "2024-01-15T11:30:00Z",
  modified_by: {
    id: "tutorial-user",
    first_name: "Demo",
    last_name: "User",
    display_name: "REQXIGN",
    email: "tutorial@example.com",
    organization_id: "tutorial-org",
  },
};

const SAMPLE_PROMPT = "顧客管理システムの主要機能を簡潔に整理してください。";

const sampleThreads = [
  {
    id: "tutorial-thread-001",
    title: "議事録の要約を確認する",
    updatedAt: "2024-01-15T11:00:00Z",
  },
  {
    id: "tutorial-thread-002",
    title: "システム概要書の改善点",
    updatedAt: "2024-01-15T11:20:00Z",
  },
  {
    id: "tutorial-thread-003",
    title: "機能追加アイデアの相談",
    updatedAt: "2024-01-15T11:45:00Z",
  },
];

const TutorialAIChat: React.FC = () => {
  useTutorialAccessGuard();
  const { currentProject } = useSelector((state: RootState) => state.auth);
  const tutorialActive =
    typeof window === "undefined" ? true : localStorage.getItem(TUTORIAL_ACTIVE_FLAG) === "true";
  const initialMessages = useMemo(() => sampleChatHistory.map(mapSampleMessage), []);

  const [messages, setMessages] = useState<ChatMessageType[]>(initialMessages);
  const [inputValue, setInputValue] = useState(SAMPLE_PROMPT);
  const [responseIndex, setResponseIndex] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>(sampleThreads[0]?.id);
  const [chatWidth, setChatWidth] = useState(() => {
    if (typeof window === "undefined") {
      return 520;
    }
    const stored = window.localStorage.getItem("tutorialAiChatWidth");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed) && parsed >= 360 && parsed <= 800) {
      return parsed;
    }
    return 520;
  });
  const [isResizing, setIsResizing] = useState(false);
  const chatWidthRef = useRef(chatWidth);

  useEffect(() => {
    chatWidthRef.current = chatWidth;
  }, [chatWidth]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      if (window.innerWidth <= 1160) {
        // 強制的に1カラムレイアウトになるため、リサイズ中であれば解除
        setIsResizing(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleSendMessage = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const userMessage: ChatMessageType = {
      id: `chat-${Date.now()}`,
      sender: "user",
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    setTimeout(() => {
      const aiMessage: ChatMessageType = {
        id: `chat-${Date.now()}-ai`,
        sender: "assistant",
        content: sampleAiResponses[responseIndex] ?? "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      setResponseIndex((prev) => (prev + 1) % sampleAiResponses.length);
    }, 600);
  };

  const handleHistory = () => {
    setIsHistoryOpen(true);
  };

  const handleNewChat = () => {
    setMessages(initialMessages);
    setInputValue(SAMPLE_PROMPT);
    setResponseIndex(0);
  };

  const handleClose = () => {
    toast.info(TOAST_MESSAGES.TUTORIAL.CLOSE_DISABLED);
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
  };

  const handleLoadThread = (thread: { id?: string; threadId?: string }) => {
    const threadId = thread.id || thread.threadId;
    if (threadId) {
      setActiveThreadId(threadId);
    }
    setMessages(initialMessages);
    setInputValue(SAMPLE_PROMPT);
    setResponseIndex(0);
    setIsHistoryOpen(false);
    toast.info(TOAST_MESSAGES.TUTORIAL.CHAT_HISTORY_LOADED);
  };

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizing(true);

    const startX = event.clientX;
    const startWidth = chatWidthRef.current;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const nextWidth = Math.min(800, Math.max(360, startWidth + delta));
      setChatWidth(nextWidth);
      chatWidthRef.current = nextWidth;
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("tutorialAiChatWidth", chatWidthRef.current.toString());
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className={styles["page"]} data-tour-id="ai-chat-content">
      <div
        className={styles["layout"]}
        style={
          {
            "--tutorial-chat-width": `${chatWidth}px`,
          } as React.CSSProperties
        }
      >
        <section className={styles["documentPane"]}>
          <CommonDocumentHeader
            title={sampleDocumentMeta.name}
            documentType="system-proposal"
            createdAt={sampleDocumentMeta.created_at}
            modifiedAt={sampleDocumentMeta.modified_at}
            modifiedBy={sampleDocumentMeta.modified_by}
            isEditable={false}
            isLoading={false}
            isHistory={true}
            handleHistoryClick={() => {}}
            showEditButton={false}
          />
          {tutorialActive && currentProject?.outputs && (
            <SystemProposalDisplay data={sampleSystemProposalData} noWrapper />
          )}
        </section>

        <aside className={styles["chatPane"]}>
          <div className={styles["chatPaneInner"]}>
            <div
              className={`${styles["resizeHandle"]} ${isResizing ? styles["isResizing"] : ""}`}
              onMouseDown={handleResizeStart}
              role="separator"
              aria-orientation="vertical"
              aria-label="チャットペインの幅を調整"
            />
            <div className={styles["historyLayout"]}>
              {isHistoryOpen && (
                <ChatHistoryPanel
                  open={isHistoryOpen}
                  threads={sampleThreads}
                  onClose={handleCloseHistory}
                  onLoadThread={handleLoadThread}
                  onRefresh={() => toast.info(TOAST_MESSAGES.TUTORIAL.HISTORY_RELOAD_DUMMY)}
                  activeThreadId={activeThreadId ?? ""}
                  placement="sidebar"
                />
              )}

              <div className={`${aiChatStyles["chatContainer"]} ${styles["chatContainer"]}`}>
                <ChatHeader
                  onClose={handleClose}
                  onHistory={handleHistory}
                  onNewChat={handleNewChat}
                  onReload={() => toast.info(TOAST_MESSAGES.TUTORIAL.CREDIT_UPDATE_DUMMY)}
                />

                <div className={aiChatStyles["chatContent"]}>
                  <div className={aiChatStyles["messagesContainer"]}>
                    {messages.map((message) => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        projectId="tutorial-sample-project"
                      />
                    ))}
                  </div>
                  <div ref={messagesEndRef} />
                </div>

                <ChatInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={handleSendMessage}
                  placeholder="AIチャットを送ってみよう（送信ボタンを押してみてください）"
                  knowledgeGenerateDisabled
                  knowledgeGenerateTooltip="チュートリアルモードでは利用不可です"
                  emphasizeSend
                  sendCta={inputValue.trim().length === 0 ? "送信してみよう" : ""}
                />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TutorialAIChat;
